import {
    ageAtAsDuration,
    ageToDuration,
    dateAtAge,
    deadBy,
    IRetirementPerson,
    isValidPerson,
    Profile,
    retirementDate,
} from "app/redux/Profile/model"
import * as utils from "app/utils"
import * as DateCalcs from "app/utils/DateCalcs"
import * as _ from "lodash"
import * as moment from "moment"

//const mo = moment as any
//mo.parseTwoDigitYear = (year) => {
// tslint:disable-next-line:no-debugger
//debugger
//const y = parseInt(year, 10)
//return y < 40 ? 2000 + y : 1900 + y;
//}

function deathAgeFormatter(person: IRetirementPerson) {
    return (
        person.firstName +
        " reaches life expectancy at age " +
        utils.toNiceAge(ageToDuration(person.selectedAgeExpectancy), {
            showMonths: false,
        })
    )
}

function groupDates(whens: moment.Moment[], fmt: string = "MMM YYYY") {
    const r: string[] = []
    let startSeq: moment.Moment
    let endSeq: moment.Moment
    let lookFor: moment.Moment

    const capture = () => {
        if (!startSeq) {
            return
        }
        let item = startSeq.format(fmt)
        if (!startSeq.isSame(endSeq)) {
            item += "-" + endSeq.format(fmt)
        }
        r.push(item)
    }

    _.forEach(whens, (e) => {
        if (e.isSame(lookFor)) {
            endSeq = e
        } else {
            capture()
            startSeq = endSeq = e
        }
        lookFor = e.clone().add(1, "month")
    })

    capture()
    return r.join(", ")
}

function calcDiff(
    older: moment.Moment,
    younger: moment.Moment
): moment.Duration {
    const a = utils.fractionalYearAndMonth(older)
    const b = utils.fractionalYearAndMonth(younger)
    return moment.duration(b - a, "years")
}

/**
 * Returns true if the person is still alive at the specified date.
 *
 * @param person
 * @param when
 */
function isAlive(person: IRetirementPerson, when: moment.Moment): boolean {
    if (!isValidPerson(person)) {
        return false
    }
    const deathDateNormalized = utils.fractionalYearAndMonth(deadBy(person))
    const whenNormalized = utils.fractionalYearAndMonth(when)
    return deathDateNormalized > whenNormalized
}

function isGoodMoment(when: moment.Moment) {
    if (utils.isNullOrUndefined(when)) {
        return false
    }
    if (!moment.isMoment(when)) {
        return false
    }
    const r = when.isValid()
    // tslint:disable-next-line:no-console
    //console.log('isGoodMoment of ' + when + ' yields: ' + r)
    return r
}

function isOlderThan(
    person: IRetirementPerson,
    age: moment.Duration,
    when: moment.Moment
): boolean {
    if (!isValidPerson(person)) {
        return false
    }
    const x = dateAtAge(person, age)
    const xNormalized = toCalcDate(x)
    const whenAdjusted = toCalcDate(when)
    return xNormalized.isAfter(whenAdjusted)
}

function earliestDate(sources: moment.Moment[]): moment.Moment {
    return _.minBy(sources, (d) => d.unix())
}

function excelSerialToMoment(source: number): moment.Moment {
    const sn = moment.duration(source, "days")
    const r = moment.utc("1900-01-01").add(sn)
    return r
}

function generateMonths(startWhen: moment.Moment, endWhen: moment.Moment) {
    const months = Math.ceil(
        moment.duration(endWhen.diff(startWhen)).asMonths()
    )
    return _.times(months, (x) =>
        DateCalcs.toCalcDate(startWhen.clone().add(x, "months"))
    )
}

function latestDate(sources: moment.Moment[]): moment.Moment {
    return _.maxBy(sources, (d) => d.unix())
}

function maxDuration(a: moment.Duration, b: moment.Duration): moment.Duration {
    const as = a.asSeconds()
    const bs = b.asSeconds()
    const r = as > bs ? a : b
    return r.clone()
}

function minDuration(a: moment.Duration, b: moment.Duration): moment.Duration {
    const as = a.asSeconds()
    const bs = b.asSeconds()
    const r = as < bs ? a : b
    return r.clone()
}

function numberPeopleLiving(
    folks: IRetirementPerson[],
    when: moment.Moment
): number {
    return _.reduce(
        folks,
        (acc, person) => {
            if (isAlive(person, when)) {
                acc++
            }
            return acc
        },
        0
    )
}

function beginSSAFormatter(person: IRetirementPerson) {
    return (
        person.firstName +
        " begins Social Security benefits at age " +
        utils.toNiceAge(ageToDuration(person.selectedRetirementAge))
    )
}

function memberDeathAgeFormatter(profile: Profile) {
    const member = profile.getHeadOfHousehold()
    return deathAgeFormatter(member)
}

function memberBeginSSAFormatter(profile: Profile) {
    const member = profile.getHeadOfHousehold()
    return beginSSAFormatter(member)
}

function memberRetirementAgeFormatter(profile: Profile) {
    const member = profile.getHeadOfHousehold()
    return retirementAgeFormatter(member)
}

function personIsDeadButIsSurvivedBy(
    when: moment.Moment,
    person: IRetirementPerson,
    survivor: IRetirementPerson
): boolean {
    return !isAlive(person, when) && isAlive(survivor, when)
}

function retirementAgeFormatter(person: IRetirementPerson) {
    return (
        person.firstName +
        " retires at age " +
        utils.toNiceAge(ageToDuration(person.selectedRetirementAge))
    )
}

function spouseDeathAgeFormatter(profile: Profile) {
    const spouse = profile.getSpouse()
    if (!isValidPerson(spouse)) {
        return ""
    }
    return deathAgeFormatter(spouse)
}

function spouseRetirementAgeFormatter(profile: Profile) {
    const spouse = profile.getSpouse()
    if (!isValidPerson(spouse)) {
        return ""
    }
    return retirementAgeFormatter(spouse)
}

function spouseBeginSSAFormatter(profile: Profile) {
    const spouse = profile.getSpouse()
    if (!isValidPerson(spouse)) {
        return ""
    }
    return beginSSAFormatter(spouse)
}

/**
 * Converts a moment to one which can be used in calculations
 *
 * @param when The date to convert
 */
function toCalcDate(when: moment.Moment): moment.Moment {
    // back up one more day (exported transactions to Excel show wrong date)
    return when.clone().endOf("month").subtract(1, "day")
}

enum WhatHappenedOn {
    memberRetirement,
    memberDeath,
    spouseRetirement,
    spouseDeath,
}

function whatHappenedOn(
    when: moment.Moment,
    profile: Profile
): WhatHappenedOn[] {
    const r: WhatHappenedOn[] = []

    const member = profile.getHeadOfHousehold()
    if (when.isSame(retirementDate(member), "month")) {
        r.push(WhatHappenedOn.memberRetirement)
    }
    if (when.isSame(deadBy(member), "month")) {
        r.push(WhatHappenedOn.memberDeath)
    }

    const spouse = profile.getSpouse()
    if (isValidPerson(spouse)) {
        if (when.isSame(retirementDate(spouse), "month")) {
            r.push(WhatHappenedOn.spouseRetirement)
        }
        if (when.isSame(deadBy(spouse), "month")) {
            r.push(WhatHappenedOn.spouseDeath)
        }
    }
    return r
}

interface IWhatHappenedFormatters {
    [xxx: number]: (profile: Profile) => string
}
const ssaWhatHappenedAgeFormatters: IWhatHappenedFormatters = {
    [WhatHappenedOn.memberRetirement]: memberBeginSSAFormatter,
    [WhatHappenedOn.memberDeath]: memberDeathAgeFormatter,
    [WhatHappenedOn.spouseRetirement]: spouseBeginSSAFormatter,
    [WhatHappenedOn.spouseDeath]: spouseDeathAgeFormatter,
}
const whatHappenedAgeFormatters: IWhatHappenedFormatters = {
    [WhatHappenedOn.memberRetirement]: memberRetirementAgeFormatter,
    [WhatHappenedOn.memberDeath]: memberDeathAgeFormatter,
    [WhatHappenedOn.spouseRetirement]: spouseRetirementAgeFormatter,
    [WhatHappenedOn.spouseDeath]: spouseDeathAgeFormatter,
}

export {
    calcDiff,
    deathAgeFormatter,
    earliestDate,
    excelSerialToMoment,
    generateMonths,
    groupDates,
    isAlive,
    isGoodMoment,
    isOlderThan,
    latestDate,
    maxDuration,
    minDuration,
    memberDeathAgeFormatter,
    memberRetirementAgeFormatter,
    numberPeopleLiving,
    personIsDeadButIsSurvivedBy,
    retirementAgeFormatter,
    spouseDeathAgeFormatter,
    spouseRetirementAgeFormatter,
    toCalcDate,
    WhatHappenedOn,
    whatHappenedOn,
    IWhatHappenedFormatters,
    ssaWhatHappenedAgeFormatters,
    whatHappenedAgeFormatters,
}



// WEBPACK FOOTER //
// ./src/app/utils/DateCalcs/index.ts